/**
 * Frontend Session Cache Manager
 * Provides localStorage-based caching for sessions and messages
 * No backend state storage - pure frontend UX enhancement
 */

// Cache configuration
const CACHE_CONFIG = {
  SESSIONS_KEY: 'bedrock-sessions',
  MESSAGES_PREFIX: 'bedrock-messages-',
  CACHE_VERSION_KEY: 'bedrock-cache-version',
  CURRENT_VERSION: '1.0',
  MAX_AGE_DAYS: 30,
  MAX_MESSAGES_PER_SESSION: 100,
};

// Type definitions
export interface CachedSession {
  sessionId: string;
  name: string;
  persona: string;
  createdAt: string;
  lastActivity: string;
  messageCount: number;
  status: 'active' | 'completed';
  firstMessage?: string;
}

export interface CachedMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  sessionId: string;
}

export interface BackendSession {
  sessionId: string;
  name: string;
  persona?: string;
  createdAt: string;
  status?: string;
  summary?: string;
}

class SessionCacheManager {
  private isLocalStorageAvailable(): boolean {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  private checkCacheVersion(): void {
    if (!this.isLocalStorageAvailable()) return;

    const currentVersion = localStorage.getItem(CACHE_CONFIG.CACHE_VERSION_KEY);
    if (currentVersion !== CACHE_CONFIG.CURRENT_VERSION) {
      // Clear old cache on version mismatch
      this.clearAllCache();
      localStorage.setItem(CACHE_CONFIG.CACHE_VERSION_KEY, CACHE_CONFIG.CURRENT_VERSION);
    }
  }

  private generateSessionName(firstMessage: string, persona: string): string {
    if (!firstMessage) return `New ${persona} conversation`;

    const cleaned = firstMessage.trim();
    const truncated = cleaned.substring(0, 50);
    return truncated.length < cleaned.length ? `${truncated}...` : truncated;
  }

  private generateMessageId(): string {
    return `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Initialize cache manager and perform cleanup
   */
  initialize(): void {
    this.checkCacheVersion();
    this.cleanupOldSessions();
  }

  /**
   * Create a new session in cache
   */
  createSession(sessionId: string, persona: string, firstMessage: string): void {
    if (!this.isLocalStorageAvailable()) return;

    const now = new Date().toISOString();
    const session: CachedSession = {
      sessionId,
      name: this.generateSessionName(firstMessage, persona),
      persona,
      createdAt: now,
      lastActivity: now,
      messageCount: 0,
      status: 'active',
      firstMessage,
    };

    const sessions = this.getCachedSessions();
    const existingIndex = sessions.findIndex((s) => s.sessionId === sessionId);

    if (existingIndex >= 0) {
      // Update existing session
      sessions[existingIndex] = { ...sessions[existingIndex], ...session };
    } else {
      // Add new session
      sessions.unshift(session);
    }

    localStorage.setItem(CACHE_CONFIG.SESSIONS_KEY, JSON.stringify(sessions));
  }

  /**
   * Add a message to session cache
   */
  addMessage(sessionId: string, message: Omit<CachedMessage, 'id' | 'sessionId'>): void {
    if (!this.isLocalStorageAvailable()) return;

    const fullMessage: CachedMessage = {
      ...message,
      id: this.generateMessageId(),
      sessionId,
    };

    // Get existing messages
    const messages = this.getSessionMessages(sessionId);
    messages.push(fullMessage);

    // Limit message count per session
    if (messages.length > CACHE_CONFIG.MAX_MESSAGES_PER_SESSION) {
      messages.splice(0, messages.length - CACHE_CONFIG.MAX_MESSAGES_PER_SESSION);
    }

    // Save messages
    const messagesKey = `${CACHE_CONFIG.MESSAGES_PREFIX}${sessionId}`;
    localStorage.setItem(messagesKey, JSON.stringify(messages));

    // Update session metadata
    this.updateSessionActivity(sessionId, messages.length);
  }

  /**
   * Get all cached sessions
   */
  getCachedSessions(): CachedSession[] {
    if (!this.isLocalStorageAvailable()) return [];

    try {
      const sessionsJson = localStorage.getItem(CACHE_CONFIG.SESSIONS_KEY);
      if (!sessionsJson) return [];

      const sessions: CachedSession[] = JSON.parse(sessionsJson);
      return sessions.sort((a, b) => new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime());
    } catch (error) {
      console.error('Error loading cached sessions:', error);
      return [];
    }
  }

  /**
   * Get messages for a specific session
   */
  getSessionMessages(sessionId: string): CachedMessage[] {
    if (!this.isLocalStorageAvailable()) return [];

    try {
      const messagesKey = `${CACHE_CONFIG.MESSAGES_PREFIX}${sessionId}`;
      const messagesJson = localStorage.getItem(messagesKey);
      if (!messagesJson) return [];

      const messages: CachedMessage[] = JSON.parse(messagesJson);
      return messages.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    } catch (error) {
      console.error('Error loading session messages:', error);
      return [];
    }
  }

  /**
   * Update session activity timestamp and message count
   */
  updateSessionActivity(sessionId: string, messageCount?: number): void {
    if (!this.isLocalStorageAvailable()) return;

    const sessions = this.getCachedSessions();
    const sessionIndex = sessions.findIndex((s) => s.sessionId === sessionId);

    if (sessionIndex >= 0) {
      sessions[sessionIndex].lastActivity = new Date().toISOString();
      if (messageCount !== undefined) {
        sessions[sessionIndex].messageCount = messageCount;
      }
      localStorage.setItem(CACHE_CONFIG.SESSIONS_KEY, JSON.stringify(sessions));
    }
  }

  /**
   * Mark session as completed (when it appears in backend summaries)
   */
  markSessionCompleted(sessionId: string): void {
    if (!this.isLocalStorageAvailable()) return;

    const sessions = this.getCachedSessions();
    const sessionIndex = sessions.findIndex((s) => s.sessionId === sessionId);

    if (sessionIndex >= 0) {
      sessions[sessionIndex].status = 'completed';
      localStorage.setItem(CACHE_CONFIG.SESSIONS_KEY, JSON.stringify(sessions));
    }
  }

  /**
   * Merge cached sessions with backend session summaries
   */
  mergeWithBackendSummaries(backendSessions: BackendSession[]): CachedSession[] {
    const cachedSessions = this.getCachedSessions();
    const mergedSessions: CachedSession[] = [...cachedSessions];

    // Add backend sessions that aren't in cache
    for (const backendSession of backendSessions) {
      const existingIndex = mergedSessions.findIndex((s) => s.sessionId === backendSession.sessionId);

      if (existingIndex >= 0) {
        // Update existing cached session with backend data
        mergedSessions[existingIndex] = {
          ...mergedSessions[existingIndex],
          status: 'completed',
          name: backendSession.name || mergedSessions[existingIndex].name,
        };
        this.markSessionCompleted(backendSession.sessionId);
      } else {
        // Add new session from backend
        const newSession: CachedSession = {
          sessionId: backendSession.sessionId,
          name: backendSession.name,
          persona: backendSession.persona || 'advisor',
          createdAt: backendSession.createdAt,
          lastActivity: backendSession.createdAt,
          messageCount: 0,
          status: 'completed',
        };
        mergedSessions.push(newSession);
      }
    }

    // Sort by last activity
    const sortedSessions = mergedSessions.sort(
      (a, b) => new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime(),
    );

    // Update the cache with merged sessions to prevent duplicates on subsequent calls
    if (this.isLocalStorageAvailable()) {
      localStorage.setItem(CACHE_CONFIG.SESSIONS_KEY, JSON.stringify(sortedSessions));
    }

    return sortedSessions;
  }

  /**
   * Delete a session and its messages from cache
   */
  deleteSession(sessionId: string): void {
    if (!this.isLocalStorageAvailable()) return;

    // Remove session from sessions list
    const sessions = this.getCachedSessions();
    const filteredSessions = sessions.filter((s) => s.sessionId !== sessionId);
    localStorage.setItem(CACHE_CONFIG.SESSIONS_KEY, JSON.stringify(filteredSessions));

    // Remove session messages
    const messagesKey = `${CACHE_CONFIG.MESSAGES_PREFIX}${sessionId}`;
    localStorage.removeItem(messagesKey);
  }

  /**
   * Clean up old sessions (older than MAX_AGE_DAYS)
   */
  cleanupOldSessions(): void {
    if (!this.isLocalStorageAvailable()) return;

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - CACHE_CONFIG.MAX_AGE_DAYS);
    const cutoffTime = cutoffDate.getTime();

    const sessions = this.getCachedSessions();
    const validSessions = sessions.filter((session) => {
      const sessionTime = new Date(session.lastActivity).getTime();
      const isValid = sessionTime > cutoffTime;

      if (!isValid) {
        // Remove messages for expired session
        const messagesKey = `${CACHE_CONFIG.MESSAGES_PREFIX}${session.sessionId}`;
        localStorage.removeItem(messagesKey);
      }

      return isValid;
    });

    if (validSessions.length !== sessions.length) {
      localStorage.setItem(CACHE_CONFIG.SESSIONS_KEY, JSON.stringify(validSessions));
      console.log(`Cleaned up ${sessions.length - validSessions.length} old sessions`);
    }
  }

  /**
   * Clear all cache data
   */
  clearAllCache(): void {
    if (!this.isLocalStorageAvailable()) return;

    // Remove all session-related keys
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (
        key === CACHE_CONFIG.SESSIONS_KEY ||
        key.startsWith(CACHE_CONFIG.MESSAGES_PREFIX) ||
        key === CACHE_CONFIG.CACHE_VERSION_KEY
      ) {
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { sessionCount: number; totalMessages: number; cacheSize: string } {
    if (!this.isLocalStorageAvailable()) {
      return { sessionCount: 0, totalMessages: 0, cacheSize: '0 KB' };
    }

    const sessions = this.getCachedSessions();
    let totalMessages = 0;
    let totalSize = 0;

    sessions.forEach((session) => {
      const messages = this.getSessionMessages(session.sessionId);
      totalMessages += messages.length;
    });

    // Estimate cache size
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key === CACHE_CONFIG.SESSIONS_KEY || key.startsWith(CACHE_CONFIG.MESSAGES_PREFIX)) {
        const value = localStorage.getItem(key);
        if (value) {
          totalSize += value.length * 2; // Rough estimate (UTF-16)
        }
      }
    });

    const cacheSize =
      totalSize < 1024
        ? `${totalSize} B`
        : totalSize < 1024 * 1024
          ? `${Math.round(totalSize / 1024)} KB`
          : `${Math.round(totalSize / (1024 * 1024))} MB`;

    return {
      sessionCount: sessions.length,
      totalMessages,
      cacheSize,
    };
  }
}

// Export singleton instance
export const sessionCache = new SessionCacheManager();
